import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2, Loader2, RefreshCw, Search, XCircle } from "lucide-react";

const CONFIANZA_STYLES = {
  alta: "bg-green-100 text-green-800 border-green-300",
  media: "bg-amber-100 text-amber-800 border-amber-300",
  baja: "bg-orange-100 text-orange-800 border-orange-300",
  manual: "bg-blue-100 text-blue-800 border-blue-300",
};
const DIRECCION_LABELS = { cobros_clientes: "Cobro de cliente", pagos_carriers: "Pago a carrier" };

function Money({ v, c }) { return <span className="font-mono">${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })} {c}</span>; }

function ManualMatchDialog({ open, onClose, match, onSaved }) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [costs, setCosts] = useState([]);
  const [selectedCostId, setSelectedCostId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setTerm(""); setResults([]); setSelectedLoad(null); setCosts([]); setSelectedCostId(null); }, [open]);

  const search = async () => {
    try {
      const { data } = await api.get("/loads", { params: { search: term, limit: 8 } });
      setResults(data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const pickLoad = async (load) => {
    setSelectedLoad(load);
    if (match?.direccion === "pagos_carriers") {
      try {
        const { data } = await api.get(`/loads/${load.id}`);
        setCosts((data.costos || []).filter((c) => c.estado_pago === "pendiente" && !c.anulado));
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }
  };

  const save = async () => {
    if (!selectedLoad) return;
    setSaving(true);
    try {
      await api.post(`/quickbooks/matches/${match.id}/manual`, {
        load_id: selectedLoad.id, cost_id: match?.direccion === "pagos_carriers" ? selectedCostId : null,
      });
      toast.success("Coincidencia asignada manualmente");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="qbo-manual-match-dialog">
        <DialogHeader><DialogTitle className="font-heading">Buscar load manualmente</DialogTitle></DialogHeader>
        <DialogDescription>Buscá un load por número o referencia y asignalo como candidato de esta coincidencia de QuickBooks.</DialogDescription>
        <div className="flex gap-2">
          <Input placeholder="Número de load o referencia" value={term} onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()} data-testid="qbo-manual-search-input" />
          <Button variant="outline" onClick={search} data-testid="qbo-manual-search-btn"><Search className="h-4 w-4" /></Button>
        </div>
        <div className="max-h-40 space-y-1 overflow-y-auto">
          {results.map((l) => (
            <button key={l.id} onClick={() => pickLoad(l)}
                    className={`block w-full rounded-md border px-3 py-2 text-left text-sm ${selectedLoad?.id === l.id ? "border-primary bg-green-50" : "border-border"}`}
                    data-testid={`qbo-manual-result-${l.id}`}>
              <b>{l.numero_load}</b> — {l.referencia_cliente} — {l.origen_ciudad}→{l.destino_ciudad}
            </button>
          ))}
        </div>
        {match?.direccion === "pagos_carriers" && selectedLoad && (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Elegí el costo pendiente de pago dentro de este load:</p>
            {costs.length === 0 && <p className="text-xs text-amber-700">Este load no tiene costos pendientes de pago.</p>}
            {costs.map((c) => (
              <label key={c.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                <input type="radio" name="cost" checked={selectedCostId === c.id} onChange={() => setSelectedCostId(c.id)} />
                {c.tipo_costo} — <Money v={c.monto} c={c.moneda} /> — {c.concepto}
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving || !selectedLoad || (match?.direccion === "pagos_carriers" && !selectedCostId)}
                  data-testid="qbo-manual-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Asignar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkConfirmDialog({ open, items, onClose, onConfirmed }) {
  const [confirming, setConfirming] = useState(false);
  const run = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/quickbooks/matches/bulk-confirm", { match_ids: items.map((i) => i.id) });
      toast.success(`${data.aplicadas} confirmada(s), ${data.rechazadas} rechazada(s)`);
      onConfirmed();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setConfirming(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="qbo-bulk-confirm-dialog">
        <DialogHeader><DialogTitle className="font-heading">Confirmar {items.length} coincidencia(s) de confianza alta</DialogTitle></DialogHeader>
        <DialogDescription>Revisá una por una las coincidencias que se van a aplicar antes de confirmar el lote.</DialogDescription>
        <div className="max-h-64 space-y-2 overflow-y-auto">
          {items.map((m) => (
            <div key={m.id} className="rounded-md border border-border p-2 text-xs">
              <b>{DIRECCION_LABELS[m.direccion]}</b> — {m.qbo_party_name} — <Money v={m.qbo_amount} c={m.qbo_currency} /> — {m.qbo_txn_date}
              {m.candidate_numero_load && <> → load {m.candidate_numero_load}</>}
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Cada una se revalida individualmente contra la base antes de aplicarse; si algún load/costo ya fue cobrado/pagado por otra vía, esa fila se rechaza y las demás siguen.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={run} disabled={confirming} data-testid="qbo-bulk-confirm-run">
            {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirmar ${items.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function QuickBooksMatches() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [direccion, setDireccion] = useState("");
  const [estado, setEstado] = useState("propuesta");
  const [matches, setMatches] = useState({ total: 0, items: [] });
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [manualMatch, setManualMatch] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [syncLog, setSyncLog] = useState({ total: 0, items: [] });

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
      if (data.length && !companyId) setCompanyId(data[0].id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const loadMatches = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/quickbooks/matches", {
        params: { company_id: companyId, direccion, estado, limit: 100 },
      });
      setMatches(data);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [companyId, direccion, estado]);
  useEffect(() => { loadMatches(); }, [loadMatches]);

  const loadSyncLog = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get("/quickbooks/sync-log", { params: { company_id: companyId, limit: 10 } });
      setSyncLog(data);
    } catch (e) { /* silencioso, no es crítico para la pantalla principal */ }
  }, [companyId]);
  useEffect(() => { loadSyncLog(); }, [loadSyncLog]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/quickbooks/connections/${companyId}/sync`, {});
      toast.success(data.mensaje);
      setTimeout(() => { loadMatches(); loadSyncLog(); }, 3000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const confirm = async (m) => {
    try {
      await api.post(`/quickbooks/matches/${m.id}/confirm`);
      toast.success("Coincidencia confirmada");
      loadMatches();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const discard = async (m) => {
    const motivo = window.prompt("Motivo (opcional):", "") || "";
    try {
      await api.post(`/quickbooks/matches/${m.id}/discard`, { motivo });
      toast.success("Coincidencia descartada");
      loadMatches();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reopen = async (m) => {
    try {
      await api.post(`/quickbooks/matches/${m.id}/reopen`);
      toast.success("Coincidencia reabierta");
      loadMatches();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const selectedAltaItems = matches.items.filter((m) => selected.has(m.id) && m.confianza === "alta");

  const exportLog = (fmt) => {
    window.open(`${api.defaults.baseURL}/quickbooks/sync-log/export/${fmt}?company_id=${companyId}`, "_blank");
  };

  return (
    <div>
      <PageHeader title="Coincidencias QuickBooks" subtitle="Revisión y confirmación manual de cobros/pagos traídos de QuickBooks Online — nunca se marca nada automáticamente." />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <select className="rounded-md border border-input bg-background px-3 py-2 text-sm" value={companyId}
                onChange={(e) => setCompanyId(e.target.value)} data-testid="qbo-matches-company-select">
          {companies.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
        <Button variant="outline" onClick={syncNow} disabled={syncing || !companyId} className="gap-2" data-testid="qbo-matches-sync-btn">
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sincronizar ahora
        </Button>
        {selectedAltaItems.length > 0 && (
          <Button onClick={() => setBulkOpen(true)} className="gap-2" data-testid="qbo-matches-bulk-open">
            <CheckCircle2 className="h-4 w-4" /> Confirmar {selectedAltaItems.length} seleccionada(s)
          </Button>
        )}
      </div>

      <Tabs value={estado} onValueChange={setEstado}>
        <TabsList>
          <TabsTrigger value="propuesta" data-testid="qbo-tab-propuesta">Propuestas</TabsTrigger>
          <TabsTrigger value="sin_coincidencia" data-testid="qbo-tab-sin-match">Sin coincidencia</TabsTrigger>
          <TabsTrigger value="confirmada" data-testid="qbo-tab-confirmada">Confirmadas</TabsTrigger>
          <TabsTrigger value="descartada" data-testid="qbo-tab-descartada">Descartadas</TabsTrigger>
        </TabsList>

        <TabsContent value={estado} className="mt-4 space-y-3">
          <div className="flex gap-2">
            {["", "cobros_clientes", "pagos_carriers"].map((d) => (
              <Button key={d} size="sm" variant={direccion === d ? "default" : "outline"} onClick={() => setDireccion(d)}
                      data-testid={`qbo-filter-direccion-${d || "todas"}`}>
                {d ? DIRECCION_LABELS[d] : "Todas"}
              </Button>
            ))}
          </div>

          {loading && <p className="text-sm text-muted-foreground">Cargando...</p>}
          {!loading && matches.items.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="qbo-matches-empty">No hay coincidencias en este estado.</p>
          )}

          {matches.items.map((m) => (
            <Card key={m.id} data-testid={`qbo-match-${m.id}`}>
              <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-[auto_1fr_1fr_auto]">
                {estado === "propuesta" && m.confianza === "alta" && (
                  <Checkbox checked={selected.has(m.id)} onCheckedChange={() => toggleSelect(m.id)}
                            data-testid={`qbo-match-select-${m.id}`} />
                )}
                <div className="space-y-1 text-sm">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">QuickBooks ({m.qbo_entity})</p>
                  <p><Money v={m.qbo_amount} c={m.qbo_currency} /> — {m.qbo_txn_date}</p>
                  <p>{m.qbo_party_name}</p>
                  {m.qbo_doc_number && <p className="text-xs text-muted-foreground">Doc: {m.qbo_doc_number}</p>}
                  {m.qbo_no_aplicado && <p className="text-xs text-blue-700">Pago no aplicado a ninguna factura en QBO</p>}
                </div>
                <div className="space-y-1 text-sm">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Candidato en el sistema</p>
                  {m.candidate_load_id || m.candidate_numero_load ? (
                    <p>Load <b>{m.candidate_numero_load || "—"}</b></p>
                  ) : <p className="text-muted-foreground">Sin candidato</p>}
                  {m.confianza && (
                    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-semibold ${CONFIANZA_STYLES[m.confianza]}`}
                          data-testid={`qbo-match-confianza-${m.id}`}>
                      Confianza {m.confianza}
                    </span>
                  )}
                  {m.criterio_texto && <p className="text-xs text-muted-foreground">{m.criterio_texto}</p>}
                </div>
                <div className="flex flex-col items-end justify-center gap-2">
                  {estado === "propuesta" && (
                    <>
                      {m.candidate_load_id && (
                        <Button size="sm" onClick={() => confirm(m)} className="gap-1" data-testid={`qbo-match-confirm-${m.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Confirmar
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setManualMatch(m)} data-testid={`qbo-match-manual-${m.id}`}>Buscar manual</Button>
                      <Button size="sm" variant="ghost" className="text-red-600 gap-1" onClick={() => discard(m)} data-testid={`qbo-match-discard-${m.id}`}>
                        <XCircle className="h-3.5 w-3.5" /> Descartar
                      </Button>
                    </>
                  )}
                  {estado === "sin_coincidencia" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setManualMatch(m)} data-testid={`qbo-match-manual-${m.id}`}>Buscar manual</Button>
                      <Button size="sm" variant="ghost" className="text-red-600 gap-1" onClick={() => discard(m)} data-testid={`qbo-match-discard-${m.id}`}>
                        <XCircle className="h-3.5 w-3.5" /> Descartar
                      </Button>
                    </>
                  )}
                  {estado === "descartada" && (
                    <Button size="sm" variant="outline" onClick={() => reopen(m)} data-testid={`qbo-match-reopen-${m.id}`}>Reabrir</Button>
                  )}
                  {estado === "confirmada" && (
                    <p className="text-xs text-muted-foreground">Por {m.confirmed_by}<br />{m.confirmed_at && new Date(m.confirmed_at).toLocaleString("es-MX")}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <Card className="mt-8">
        <CardHeader><CardTitle className="font-heading text-base">Log de sincronizaciones</CardTitle></CardHeader>
        <CardContent>
          <div className="mb-2 flex gap-2">
            <Button size="sm" variant="outline" onClick={() => exportLog("csv")} data-testid="qbo-export-log-csv">Exportar CSV</Button>
            <Button size="sm" variant="outline" onClick={() => exportLog("xlsx")} data-testid="qbo-export-log-xlsx">Exportar Excel</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-muted-foreground">
                <th className="p-1">Fecha</th><th className="p-1">Disparado por</th><th className="p-1">Rango</th>
                <th className="p-1">Traídos</th><th className="p-1">Alta/Media/Baja</th><th className="p-1">Sin match</th>
                <th className="p-1">Estado</th><th className="p-1">Duración</th>
              </tr></thead>
              <tbody>
                {syncLog.items.map((l) => (
                  <tr key={l.id} className="border-t border-border" data-testid={`qbo-synclog-row-${l.id}`}>
                    <td className="p-1">{l.started_at && new Date(l.started_at).toLocaleString("es-MX")}</td>
                    <td className="p-1">{l.disparado_por}</td>
                    <td className="p-1">{l.rango_desde} → {l.rango_hasta}</td>
                    <td className="p-1">{l.total_traidos}</td>
                    <td className="p-1">{l.confianza_alta}/{l.confianza_media}/{l.confianza_baja}</td>
                    <td className="p-1">{l.sin_coincidencia}</td>
                    <td className="p-1">{l.estado}{l.error ? ` — ${l.error}` : ""}</td>
                    <td className="p-1">{l.duracion_seg}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <ManualMatchDialog open={!!manualMatch} match={manualMatch} onClose={() => setManualMatch(null)} onSaved={loadMatches} />
      <BulkConfirmDialog open={bulkOpen} items={selectedAltaItems} onClose={() => setBulkOpen(false)} onConfirmed={loadMatches} />
    </div>
  );
}
